import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchOrders,
  fetchAvailableDeliveryBoys,
  assignDeliveryBoy,
  updateOrderStatus,
  updateOrderShopStatus,
} from '../services/orderService';
import { toast } from 'react-toastify';
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import MapPreview from '../components/MapPreview';
import { fetchDeliveryBoys } from '../services/deliveryBoyService';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return dateTimeFormatter.format(date);
};

const parseCoordinate = (value) => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const STATUS_OPTIONS = [
  { id: 1, name: 'Placed' },
  { id: 2, name: 'Informed to Shop' },
  { id: 3, name: 'Ready for Pickup' },
  { id: 4, name: 'Delivery Boy Assigned' },
  { id: 5, name: 'Picked Up' },
  { id: 6, name: 'Delivered' },
  { id: 7, name: 'Cancelled' },
];

const DELIVERED_STATUS_ID = 6;

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [deliveryBoyFilterOptions, setDeliveryBoyFilterOptions] = useState([]);
  const [deliveryBoyFilter, setDeliveryBoyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [statusEditing, setStatusEditing] = useState({});
  const [shopStatusUpdates, setShopStatusUpdates] = useState({});
  const [assignModal, setAssignModal] = useState(null);
  const [inlineAssignments, setInlineAssignments] = useState({});
  const [inlineAssignLoading, setInlineAssignLoading] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 10;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadOrders = async () => {
    try {
      const res = await fetchOrders({
        search,
        status: statusFilter,
        page,
        limit: LIMIT,
        deliveryBoyId: deliveryBoyFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const data = res.data?.data || {};
      setOrders(data.records || []);
      setTotalPages(data.pages || 1);
      setShopStatusUpdates({});
      setExpanded(null);
      setInlineAssignments({});
      setInlineAssignLoading({});
      setStatusUpdates({});
      setStatusEditing({});
    } catch (err) {
      toast.error('Failed to load orders');
    }
  };

  const loadDeliveryBoys = async () => {
    try {
      const res = await fetchAvailableDeliveryBoys();
      setDeliveryBoys(res.data?.data?.records || res.data?.data || res.data || []);
    } catch (err) {
      toast.error('Failed to load delivery executives');
    }
    try {
      const res = await fetchDeliveryBoys({ limit: 200, page: 1 });
      const payload = res.data?.data;
      const records = payload?.records || payload || [];
      setDeliveryBoyFilterOptions(records);
    } catch (err) {
      /* ignore filter list failures */
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadOrders();
  }, [search, statusFilter, page, deliveryBoyFilter, fromDate, toDate]);

  useEffect(() => {
    loadDeliveryBoys();
  }, []);

  const handleSearchInput = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDeliveryFilterChange = (value) => {
    setDeliveryBoyFilter(value);
    setPage(1);
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
    setPage(1);
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    setPage(1);
  };

  const handleClearDateFilters = () => {
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const getAssignmentSummary = (order) => {
    const orderLevelExecutives = (order.deliveryAssignments || [])
      .map((assignment) => assignment.deliveryBoy?.name)
      .filter(Boolean);
    if (orderLevelExecutives.length > 0) {
      const unique = [...new Set(orderLevelExecutives)];
      if (unique.length === 1) {
        return unique[0];
      }
      return `${unique[0]} +${unique.length - 1} more`;
    }

    const shopExecutives = (order.shops || [])
      .map((shop) => shop.deliveryBoy?.name)
      .filter(Boolean);
    if (shopExecutives.length === 0) {
      return '-';
    }
    const uniqueShopExecutives = [...new Set(shopExecutives)];
    if (uniqueShopExecutives.length === 1) {
      return `${uniqueShopExecutives[0]} (shop-wise)`;
    }
    return `Multiple (${uniqueShopExecutives.length})`;
  };

  const getDefaultOrderDeliveryBoyId = (order) => {
    if (!order) return '';
    const orderAssignmentIds = (order.deliveryAssignments || [])
      .map((assignment) => assignment.deliveryBoy?.id)
      .filter(Boolean);

    if (orderAssignmentIds.length === 1) {
      return String(orderAssignmentIds[0]);
    }

    if ((order.shops || []).length === 1) {
      const [shop] = order.shops;
      if (shop?.deliveryBoy?.id) {
        return String(shop.deliveryBoy.id);
      }
    }

    return '';
  };

  const openAssignModal = (order, initialMode = 'order') => {
    if (order?.currentStatus?.id === DELIVERED_STATUS_ID) {
      return;
    }
    const shopAssignments = {};
    (order.shops || []).forEach((shop) => {
      shopAssignments[shop.id] = shop.deliveryBoy?.id
        ? String(shop.deliveryBoy.id)
        : '';
    });

    const orderAssignmentIds = (order.deliveryAssignments || [])
      .map((assignment) => assignment.deliveryBoy?.id)
      .filter(Boolean);

    let mode = initialMode;
    const hasMultipleShops = (order.shops || []).length > 1;
    if (
      initialMode === 'order' &&
      (orderAssignmentIds.length !== 1 && (order.shops || []).some((shop) => shop.deliveryBoy?.id))
    ) {
      mode = 'shop';
    }

    if (!hasMultipleShops) {
      mode = 'order';
    }

    const defaultOrderExec =
      orderAssignmentIds.length === 1 ? String(orderAssignmentIds[0]) : '';

    setAssignModal({
      order,
      mode,
      orderDeliveryBoyId: defaultOrderExec,
      shopAssignments,
      saving: false,
    });
  };

  const closeAssignModal = () => setAssignModal(null);

  const handleAssignmentModeChange = (mode) => {
    setAssignModal((prev) => {
      if (!prev) return prev;
      const hasMultipleShops = (prev.order?.shops?.length || 0) > 1;
      if (!hasMultipleShops && mode === 'shop') {
        return prev;
      }
      return { ...prev, mode };
    });
  };

  const handleOrderAssignmentChange = (value) => {
    setAssignModal((prev) => (prev ? { ...prev, orderDeliveryBoyId: value } : prev));
  };

  const handleShopAssignmentChange = (orderShopId, value) => {
    setAssignModal((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shopAssignments: {
          ...prev.shopAssignments,
          [orderShopId]: value,
        },
      };
    });
  };

  const handleAssignmentSubmit = async () => {
    if (!assignModal?.order) return;

    try {
      setAssignModal((prev) => (prev ? { ...prev, saving: true } : prev));
      if (assignModal.mode === 'order') {
        if (!assignModal.orderDeliveryBoyId) {
          toast.error('Select a delivery executive');
          setAssignModal((prev) => (prev ? { ...prev, saving: false } : prev));
          return;
        }
        await assignDeliveryBoy(assignModal.order.id, {
          assignmentType: 'order',
          deliveryBoyId: Number(assignModal.orderDeliveryBoyId),
        });
      } else {
        const shops = assignModal.order.shops || [];
        if (!shops.length) {
          toast.error('No shops found for this order');
          setAssignModal((prev) => (prev ? { ...prev, saving: false } : prev));
          return;
        }
        const assignments = shops.map((shop) => ({
          orderShopId: shop.id,
          deliveryBoyId: Number(assignModal.shopAssignments[shop.id]),
        }));
        if (assignments.some((assignment) => !assignment.deliveryBoyId)) {
          toast.error('Assign a delivery executive for every shop');
          setAssignModal((prev) => (prev ? { ...prev, saving: false } : prev));
          return;
        }
        await assignDeliveryBoy(assignModal.order.id, {
          assignmentType: 'shop',
          shopAssignments: assignments,
        });
      }

      toast.success('Delivery executive assigned');
      setAssignModal(null);
      loadOrders();
    } catch (err) {
      console.error(err);
      toast.error('Failed to assign');
      setAssignModal((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  };

  const openStatusEditor = (order) => {
    if (!order) return;
    if (order.currentStatus?.id === DELIVERED_STATUS_ID) {
      toast.error('Delivered orders cannot be updated');
      return;
    }
    setStatusEditing((prev) => ({ ...prev, [order.id]: true }));
    setStatusUpdates((prev) => ({
      ...prev,
      [order.id]: prev[order.id] || '',
    }));
  };

  const cancelStatusEditor = (orderId) => {
    setStatusEditing((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
    setStatusUpdates((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  const handleStatusUpdate = async (order) => {
    if (!order) return;

    if (order.currentStatus?.id === DELIVERED_STATUS_ID) {
      toast.error('Delivered orders cannot be updated');
      return;
    }

    const statusId = statusUpdates[order.id];
    if (!statusId) {
      toast.error('Select status');
      return;
    }

    const shops = order.shops || [];
    if (shops.length > 0) {
      const confirmationMessage =
        shops.length === 1
          ? 'Updating the order status will also update the status for this shop. Do you want to continue?'
          : `Updating the order status will also update the status for all ${shops.length} shops in this order. Do you want to continue?`;
      const confirmed = window.confirm(confirmationMessage);
      if (!confirmed) {
        return;
      }
    }

    try {
      await updateOrderStatus(order.id, statusId);
      if (shops.length > 0) {
        await Promise.all(
          shops.map((shop) =>
            updateOrderShopStatus(order.id, shop.id, statusId)
          )
        );
      }
      toast.success('Status updated');
      setStatusUpdates((s) => ({ ...s, [order.id]: '' }));
      cancelStatusEditor(order.id);
      loadOrders();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleShopStatusUpdate = async (orderId, orderShopId) => {
    const order = orders.find((o) => o.id === orderId);
    const shop = order?.shops?.find((s) => s.id === orderShopId);

    if (shop?.currentStatus?.id === DELIVERED_STATUS_ID) {
      toast.error('Delivered shop orders cannot be updated');
      return;
    }

    const statusId = shopStatusUpdates[orderShopId];
    if (!statusId) {
      toast.error('Select status');
      return;
    }

    try {
      await updateOrderShopStatus(orderId, orderShopId, statusId);
      toast.success('Shop status updated');
      setShopStatusUpdates((prev) => ({ ...prev, [orderShopId]: '' }));
      loadOrders();
    } catch (err) {
      toast.error('Failed to update shop status');
    }
  };

  const handleInlineAssignment = async (order) => {
    if (!order) return;

    if (order.currentStatus?.id === DELIVERED_STATUS_ID) {
      toast.info('Delivered orders do not require assignment updates');
      return;
    }

    const selectedValue = inlineAssignments[order.id] ?? getDefaultOrderDeliveryBoyId(order);
    if (!selectedValue) {
      toast.error('Select a delivery executive');
      return;
    }

    try {
      setInlineAssignLoading((prev) => ({ ...prev, [order.id]: true }));
      await assignDeliveryBoy(order.id, {
        assignmentType: 'order',
        deliveryBoyId: Number(selectedValue),
      });
      toast.success('Delivery executive assigned');
      setInlineAssignments((prev) => ({ ...prev, [order.id]: '' }));
      await loadOrders();
    } catch (err) {
      toast.error('Failed to assign delivery executive');
    } finally {
      setInlineAssignLoading((prev) => ({ ...prev, [order.id]: false }));
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-4">Orders</h1>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
          <div className="relative md:w-1/3">
            <Search className="absolute left-2 top-2.5 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search by order ID or customer"
              value={search}
              onChange={(e) => handleSearchInput(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border rounded dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:ml-4">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="border rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={deliveryBoyFilter}
              onChange={(e) => handleDeliveryFilterChange(e.target.value)}
              className="border rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
            >
              <option value="">All Delivery Executives</option>
              {deliveryBoyFilterOptions.map((boy) => (
                <option key={boy.id} value={boy.id}>
                  {boy.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
          <div className="flex items-center gap-2">
            <label htmlFor="fromDate" className="text-sm text-gray-600 dark:text-gray-300">
              From
            </label>
            <input
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              className="border rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="toDate" className="text-sm text-gray-600 dark:text-gray-300">
              To
            </label>
            <input
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => handleToDateChange(e.target.value)}
              className="border rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <button
            type="button"
            onClick={handleClearDateFilters}
            className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 disabled:opacity-50"
            disabled={!fromDate && !toDate}
          >
            Clear dates
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white dark:bg-gray-800">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700 text-left">
                <th className="py-2 px-3"></th>
                <th className="py-2 px-3">Created</th>
                <th className="py-2 px-3">Order ID</th>
                <th className="py-2 px-3">Customer</th>
                <th className="py-2 px-3">Total</th>
                <th className="py-2 px-3">Delivery Charge</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Assigned To</th>
                <th className="py-2 px-3">Assign</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const shopCount = o.shops?.length || 0;
                const isExpanded = expanded === o.id;
                const payments = Array.isArray(o.payments) ? o.payments : [];
                const totalPaid = payments.reduce(
                  (sum, payment) => sum + Number(payment?.amountPaid || 0),
                  0,
                );
                const finalAmount =
                  Number(o.totalAmount || 0) +
                  Number(o.deliveryCharge || 0) -
                  Number(o.discount || 0);
                const outstandingAmount = Math.max(finalAmount - totalPaid, 0);
                const address = o.customerAddress || null;
                const addressLatitude = address ? parseCoordinate(address.latitude) : null;
                const addressLongitude = address ? parseCoordinate(address.longitude) : null;
                const hasCoordinates = addressLatitude !== null && addressLongitude !== null;
                const googleMapsUrl = hasCoordinates
                  ? `https://www.google.com/maps/search/?api=1&query=${addressLatitude},${addressLongitude}`
                  : null;
                const assignmentSummary = getAssignmentSummary(o);
                const defaultOrderExecId = getDefaultOrderDeliveryBoyId(o);
                const inlineAssignmentValue =
                  inlineAssignments[o.id] ?? defaultOrderExecId;
                const inlineAssignmentLoading = inlineAssignLoading[o.id];
                const isDelivered = o.currentStatus?.id === DELIVERED_STATUS_ID;
                const isEditingStatus = Boolean(statusEditing[o.id]);
                const statusSelection = statusUpdates[o.id] || '';
                const createdAtValue = o.placedAt || o.createdAt || o.created_at || null;
                return (
                  <React.Fragment key={o.id}>
                    <tr className="border-t dark:border-gray-700">
                      <td className="py-2 px-3">
                        <button
                          onClick={() => setExpanded(isExpanded ? null : o.id)}
                          className="text-indigo-600"
                          aria-label={isExpanded ? 'Collapse order row' : 'Expand order row'}
                        >
                          {isExpanded ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(createdAtValue)}</td>
                      <td className="py-2 px-3">{o.showId}</td>
                      <td
                        className="py-2 px-3"
                        title={o.customer?.phone ? `Phone: ${o.customer.phone}` : undefined}
                      >
                        {o.customer?.name || ''}
                      </td>
                      <td className="py-2 px-3">{formatCurrency(o.totalAmount)}</td>
                      <td className="py-2 px-3">{formatCurrency(o.deliveryCharge)}</td>
                      <td
                        className={`py-2 px-3 align-top ${isDelivered ? '' : 'cursor-pointer'}`}
                        onDoubleClick={() => openStatusEditor(o)}
                      >
                        <div className={`space-y-2 ${isDelivered ? '' : 'group'}`}>
                          <span className="font-medium text-gray-800 dark:text-gray-100">
                            {o.currentStatus?.name || ''}
                          </span>
                          {isEditingStatus ? (
                            <div
                              className="space-y-2"
                              onDoubleClick={(event) => event.stopPropagation()}
                            >
                              <select
                                className="border p-1 dark:bg-gray-800 dark:text-white"
                                value={statusSelection}
                                onChange={(e) =>
                                  setStatusUpdates((s) => ({ ...s, [o.id]: e.target.value }))
                                }
                                disabled={isDelivered}
                              >
                                <option value="">Select</option>
                                {STATUS_OPTIONS.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                              </select>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleStatusUpdate(o)}
                                  className="bg-green-600 text-white px-3 py-1 rounded disabled:opacity-60"
                                  disabled={isDelivered || !statusSelection}
                                >
                                  Update
                                </button>
                                <button
                                  type="button"
                                  onClick={() => cancelStatusEditor(o.id)}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            !isDelivered && (
                              <span className="text-sm text-gray-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                                Double-click status to change
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">{assignmentSummary}</td>
                      <td className="py-2 px-3">
                        {isDelivered ? (
                          <span className="text-sm text-gray-500">Delivered</span>
                        ) : shopCount <= 1 ? (
                          <div className="flex space-x-2">
                            <select
                              className="border p-1 dark:bg-gray-800 dark:text-white"
                              value={inlineAssignmentValue || ''}
                              onChange={(e) =>
                                setInlineAssignments((prev) => ({
                                  ...prev,
                                  [o.id]: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select</option>
                              {deliveryBoys.map((boy) => (
                                <option key={boy.id} value={boy.id}>
                                  {boy.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleInlineAssignment(o)}
                              className="bg-indigo-600 text-white px-3 py-1 rounded disabled:opacity-60"
                              disabled={inlineAssignmentLoading}
                            >
                              {inlineAssignmentLoading ? 'Assigning...' : 'Assign'}
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                            <button
                              type="button"
                              onClick={() => openAssignModal(o, 'order')}
                              className="bg-indigo-600 text-white px-3 py-1 rounded"
                            >
                              Entire Order
                            </button>
                            <button
                              type="button"
                              onClick={() => openAssignModal(o, 'shop')}
                              className="bg-blue-500 text-white px-3 py-1 rounded"
                            >
                              Shop-wise
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex space-x-2">
                          <Link
                            to={`/orders/${o.id}`}
                            className="text-indigo-600"
                          >
                            View
                          </Link>
                          <Link
                            to={`/orders/${o.id}/edit`}
                            className="text-green-600"
                          >
                            Edit
                          </Link>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 dark:bg-gray-700 border-t dark:border-gray-700">
                        <td colSpan={9} className="p-4">
                          <div className="space-y-4">
                            <div className={`grid gap-4 ${hasCoordinates ? 'md:grid-cols-2' : ''}`}>
                              <div className="border border-gray-200 dark:border-gray-600 rounded p-4 bg-white dark:bg-gray-800">
                                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                                  Delivery Address
                                </h3>
                                {address ? (
                                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
                                    {address.label ? (
                                      <p>
                                        <strong>Label:</strong> {address.label}
                                      </p>
                                    ) : null}
                                    <div>
                                      <p className="font-semibold">Address:</p>
                                      <p className="whitespace-pre-line">
                                        {address.address || 'Not provided'}
                                      </p>
                                    </div>
                                    <p>
                                      <strong>Latitude:</strong>{' '}
                                      {addressLatitude !== null ? addressLatitude : 'Not available'}
                                    </p>
                                    <p>
                                      <strong>Longitude:</strong>{' '}
                                      {addressLongitude !== null ? addressLongitude : 'Not available'}
                                    </p>
                                    <div className="pt-2 space-y-1">
                                      <p>
                                        <strong>Final Amount:</strong> {formatCurrency(finalAmount)}
                                      </p>
                                      <p>
                                        <strong>Paid Amount:</strong> {formatCurrency(totalPaid)}
                                      </p>
                                      <p>
                                        <strong>Amount Due:</strong> {formatCurrency(outstandingAmount)}
                                      </p>
                                    </div>
                                    {googleMapsUrl && (
                                      <a
                                        href={googleMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-indigo-600 hover:underline"
                                      >
                                        View in Google Maps
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600 dark:text-gray-300">
                                    No delivery address recorded for this order.
                                  </p>
                                )}
                              </div>
                              {hasCoordinates ? (
                                <div className="border border-gray-200 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-800">
                                  <MapPreview lat={addressLatitude} lng={addressLongitude} height={220} />
                                </div>
                              ) : null}
                            </div>
                            {o.shops && o.shops.length ? (
                              <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                  <span className="font-medium text-sm text-gray-700 dark:text-gray-200">
                                    Shop assignments
                                  </span>
                                  {!isDelivered && (
                                    <button
                                      type="button"
                                      onClick={() => openAssignModal(o, 'shop')}
                                      className="mt-2 sm:mt-0 inline-flex items-center px-3 py-1 text-sm bg-indigo-600 text-white rounded"
                                    >
                                      Manage Shop Assignments
                                    </button>
                                  )}
                                </div>
                                {o.shops.map((s) => {
                                  const shopDelivered = s.currentStatus?.id === DELIVERED_STATUS_ID;
                                  return (
                                    <div
                                      key={s.id}
                                      className="border border-gray-200 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800"
                                    >
                                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                        <div>
                                          <div className="font-semibold text-gray-800 dark:text-gray-100">
                                            {s.shop?.name || `Shop #${s.shopId}`}
                                          </div>
                                          <div className="text-sm text-gray-600 dark:text-gray-300">
                                            Delivery Exec: {s.deliveryBoy?.name || '-'}
                                          </div>
                                          <div className="text-sm text-gray-600 dark:text-gray-300">
                                            Status: {s.currentStatus?.name || '-'}
                                          </div>
                                          <div className="text-sm text-gray-600 dark:text-gray-300">
                                            Shop Total: {formatCurrency(s.shopTotal)}
                                          </div>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                                          <select
                                            className="border p-2 rounded dark:bg-gray-700 dark:text-white flex-1"
                                            value={shopStatusUpdates[s.id] || ''}
                                            onChange={(e) =>
                                              setShopStatusUpdates((prev) => ({
                                                ...prev,
                                                [s.id]: e.target.value,
                                              }))
                                            }
                                            disabled={shopDelivered}
                                          >
                                            <option value="">Update status</option>
                                            {STATUS_OPTIONS.map((status) => (
                                              <option key={status.id} value={status.id}>
                                                {status.name}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            onClick={() => handleShopStatusUpdate(o.id, s.id)}
                                            className="bg-green-600 text-white px-3 py-1 rounded whitespace-nowrap disabled:opacity-60"
                                            disabled={shopDelivered}
                                          >
                                            Update
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">
                                No shop details
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end items-center space-x-2 mt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
        {assignModal?.order && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Assign Order {assignModal.order.showId}
                </h2>
                <button
                  type="button"
                  onClick={closeAssignModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              {(assignModal.order?.shops?.length || 0) > 1 && (
                <div className="flex space-x-6 mb-4 text-sm">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assignment-mode"
                      value="order"
                      checked={assignModal.mode === 'order'}
                      onChange={() => handleAssignmentModeChange('order')}
                    />
                    <span>Entire Order</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assignment-mode"
                      value="shop"
                      checked={assignModal.mode === 'shop'}
                      onChange={() => handleAssignmentModeChange('shop')}
                    />
                    <span>Shop-wise</span>
                  </label>
                </div>
              )}
              {assignModal.mode === 'order' ||
              (assignModal.order?.shops?.length || 0) <= 1 ? (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
                    Delivery Executive
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                    value={assignModal.orderDeliveryBoyId || ''}
                    onChange={(e) => handleOrderAssignmentChange(e.target.value)}
                  >
                    <option value="">Select delivery executive</option>
                    {deliveryBoys.map((boy) => (
                      <option key={boy.id} value={boy.id}>
                        {boy.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                  {assignModal.order.shops && assignModal.order.shops.length ? (
                    assignModal.order.shops.map((shop) => (
                      <div
                        key={shop.id}
                        className="border border-gray-200 dark:border-gray-600 rounded p-3 bg-gray-50 dark:bg-gray-700"
                      >
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                          {shop.shop?.name || `Shop #${shop.shopId}`}
                        </div>
                        <select
                          className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
                          value={assignModal.shopAssignments[shop.id] || ''}
                          onChange={(e) => handleShopAssignmentChange(shop.id, e.target.value)}
                        >
                          <option value="">Select delivery executive</option>
                          {deliveryBoys.map((boy) => (
                            <option key={boy.id} value={boy.id}>
                              {boy.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      No shops available for this order.
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={closeAssignModal}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200"
                  disabled={assignModal.saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAssignmentSubmit}
                  className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-60"
                  disabled={assignModal.saving}
                >
                  {assignModal.saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWithSidebar>
  );
}

